import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, FileText, Trash2, Edit } from 'lucide-react';
import { loanApplicationsApi } from '../../api';
import { LoanApplication, ApplicationStatus } from '../../types';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { StatusBadge } from '../../components/StatusBadge';
import { Button } from '../../components/Button';

export const MyApplications: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<LoanApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(
    (location.state as any)?.message || ''
  );

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await loanApplicationsApi.getAll();
      if (response.success && response.data) {
        setApplications(response.data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this draft application?')) {
      return;
    }

    try {
      const response = await loanApplicationsApi.delete(id);
      if (response.success) {
        setSuccessMessage('Draft application deleted successfully');
        setApplications(applications.filter((app) => app.application_id !== id));
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete application');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="applications-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Loan Applications</h1>
          <p>Track and manage all your loan application requests</p>
        </div>
        <Button onClick={() => navigate('/customer/applications/new')} variant="primary" icon={<Plus size={20} />}>
          New Application
        </Button>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {successMessage && (
        <div className="success-message">
          <span>{successMessage}</span>
        </div>
      )}

      {applications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FileText size={64} />
          </div>
          <h3>No Applications Yet</h3>
          <p>You haven't submitted any loan applications. Start your first application to get funding quickly.</p>
          <Button onClick={() => navigate('/customer/applications/new')} variant="primary" size="lg" icon={<Plus size={20} />}>
            Apply for Your First Loan
          </Button>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Product</th>
                <th>Amount Requested</th>
                <th>Term</th>
                <th>Status</th>
                <th>Date Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app.application_id}>
                  <td className="font-mono">{app.application_reference}</td>
                  <td className="font-medium">{app.product_name || 'N/A'}</td>
                  <td className="font-semibold">
                    {parseFloat(app.amount_requested).toLocaleString('en-NG', {
                      style: 'currency',
                      currency: 'NGN',
                    })}
                  </td>
                  <td>{app.loan_term_months} months</td>
                  <td>
                    <StatusBadge status={app.status} />
                  </td>
                  <td>
                    {app.submitted_at
                      ? new Date(app.submitted_at).toLocaleDateString('en-NG', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })
                      : 'Draft'}
                  </td>
                  <td>
                    {app.status === ApplicationStatus.DRAFT ? (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Button
                          onClick={() => navigate('/customer/applications/new')}
                          variant="primary"
                          size="sm"
                          icon={<Edit size={16} />}
                          title="Continue draft application"
                        >
                          Continue
                        </Button>
                        <Button
                          onClick={() => handleDelete(app.application_id)}
                          variant="danger"
                          size="sm"
                          icon={<Trash2 size={16} />}
                          title="Delete draft application"
                        >
                          Delete
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => navigate(`/customer/applications/${app.application_id}`)}
                        variant="secondary"
                        size="sm"
                        icon={<FileText size={16} />}
                      >
                        View
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        /* Tablet Responsive Styles */
        @media (max-width: 1024px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .page-header-content {
            width: 100%;
          }

          .page-header button {
            width: 100%;
          }

          .table-container {
            overflow-x: auto;
          }

          .data-table {
            min-width: 800px;
          }
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .applications-page {
            padding: 1rem;
          }

          .page-header h1 {
            font-size: 1.75rem;
          }

          .page-header p {
            font-size: 0.9375rem;
          }

          .table-container {
            border-radius: 0.75rem;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .data-table {
            min-width: 900px;
            font-size: 0.875rem;
          }

          .data-table th,
          .data-table td {
            padding: 0.75rem 0.5rem;
          }

          .data-table th:first-child,
          .data-table td:first-child {
            position: sticky;
            left: 0;
            background-color: white;
            z-index: 1;
          }

          .data-table th:first-child {
            background-color: var(--color-neutral-50);
          }

          .empty-state {
            padding: 2rem 1rem;
          }

          .empty-state h3 {
            font-size: 1.25rem;
          }

          .empty-state p {
            font-size: 0.9375rem;
          }

          .empty-state-icon svg {
            width: 48px;
            height: 48px;
          }
        }

        /* Small Mobile Responsive Styles */
        @media (max-width: 480px) {
          .applications-page {
            padding: 0.875rem;
          }

          .page-header h1 {
            font-size: 1.5rem;
          }

          .page-header button {
            padding: 0.75rem 1rem;
            font-size: 0.9375rem;
          }

          .data-table {
            font-size: 0.8125rem;
          }

          .empty-state {
            padding: 1.5rem 0.875rem;
          }

          .empty-state-icon svg {
            width: 40px;
            height: 40px;
          }
        }
      `}</style>
    </div>
  );
};
